type GameBridgeConfig = {
    tokenUrl: string | null;
    allowedOrigins: string[];
    targetOrigin: string | null;
};

type TokenErrorPayload = {
    error?: string;
};

type PresenceEnvelope = {
    messageType: "RX36_MULTI_TAB_DETECTED" | "RX36_MULTI_TAB_CLEARED";
    payload: Record<string, unknown>;
};

type FullscreenEnvelope = {
    type: "RX36_GAME_IFRAME_FULLSCREEN";
    active?: boolean;
};

const parseOrigins = (csv: string | null): string[] => {
    if (!csv) return [];
    return csv
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
};

const originFromUrl = (url: string | null): string | null => {
    if (!url) return null;
    try {
        return new URL(url, window.location.href).origin;
    } catch {
        return null;
    }
};

const tokenKindFromIframe = (iframe: HTMLIFrameElement): string | null => {
    const rawSrc = iframe.getAttribute('src');
    if (!rawSrc) return null;
    try {
        const resolvedUrl = new URL(rawSrc, window.location.href);
        const tokenKind = resolvedUrl.searchParams.get('rx36_token_kind');
        return tokenKind ? tokenKind.trim().toLowerCase() : null;
    } catch {
        return null;
    }
};

const resolveTokenUrl = (iframe: HTMLIFrameElement, fallbackTokenUrl: string | null): string | null => {
    const tokenKind = tokenKindFromIframe(iframe);
    if (tokenKind === 'english') {
        return '/api/english-game-token/';
    }
    return fallbackTokenUrl;
};

const readTokenErrorCode = async (response: Response): Promise<string> => {
    try {
        const payload = (await response.json()) as TokenErrorPayload;
        if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
            return payload.error;
        }
    } catch {
        // Fall back to the generic error code.
    }
    return 'token_unavailable';
};

const iframes = Array.from(document.querySelectorAll<HTMLIFrameElement>('iframe[data-rx36-game-embed]'));

if (iframes.length) {
    const iframeMeta = new Map<HTMLIFrameElement, GameBridgeConfig>();
    let fullscreenIframe: HTMLIFrameElement | null = null;

    iframes.forEach((iframe) => {
        const tokenUrl = iframe.getAttribute('data-rx36-token-url');
        const allowedCsv = iframe.getAttribute('data-rx36-allowed-origins') || '';
        const allowed = parseOrigins(allowedCsv);
        const targetOrigin = originFromUrl(iframe.getAttribute('src'));
        iframeMeta.set(iframe, { tokenUrl, allowedOrigins: allowed, targetOrigin });
    });

    const setIframeFullscreenState = (iframe: HTMLIFrameElement, active: boolean): void => {
        if (!active && fullscreenIframe === iframe) {
            fullscreenIframe = null;
        }

        if (active) {
            if (fullscreenIframe && fullscreenIframe !== iframe) {
                fullscreenIframe.classList.remove('article-game-page__iframe--fullscreen');
            }
            fullscreenIframe = iframe;
        }

        iframe.classList.toggle('article-game-page__iframe--fullscreen', active);
        document.body.classList.toggle('rx36-game-embed-fullscreen', active);
    };

    const isAllowedIframeEvent = (
        iframe: HTMLIFrameElement,
        meta: GameBridgeConfig,
        event: MessageEvent,
    ): boolean => {
        if (event.source !== iframe.contentWindow) return false;
        if (meta.targetOrigin && event.origin !== meta.targetOrigin) return false;
        if (meta.allowedOrigins.length && !meta.allowedOrigins.includes(event.origin)) return false;
        return true;
    };

    const parseFullscreenEnvelope = (value: unknown): FullscreenEnvelope | null => {
        if (!value || typeof value !== 'object') return null;
        const maybe = value as { type?: unknown; active?: unknown };
        if (maybe.type !== 'RX36_GAME_IFRAME_FULLSCREEN') return null;
        return {
            type: 'RX36_GAME_IFRAME_FULLSCREEN',
            active: maybe.active === true,
        };
    };

    window.addEventListener('message', async (event) => {
        for (const iframe of iframes) {
            const meta = iframeMeta.get(iframe);
            if (!meta) continue;

            if (!isAllowedIframeEvent(iframe, meta, event)) continue;

            const fullscreenEnvelope = parseFullscreenEnvelope(event.data);
            if (fullscreenEnvelope) {
                setIframeFullscreenState(iframe, fullscreenEnvelope.active === true);
                continue;
            }

            if (!event.data || typeof event.data !== 'object') continue;
            if ((event.data as { type?: string }).type !== 'RX36_GAME_READY') continue;

            const nonce = (event.data as { nonce?: string }).nonce;
            try {
                const tokenUrl = resolveTokenUrl(iframe, meta.tokenUrl);
                if (!tokenUrl) throw new Error('missing_token_url');
                const res = await fetch(tokenUrl, {
                    method: 'GET',
                    credentials: 'same-origin',
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error(await readTokenErrorCode(res));
                const payload = (await res.json()) as {
                    token?: string;
                    backend_base_url?: string;
                    exp?: number;
                    sub_hint?: string | null;
                    username_hint?: string | null;
                };

                const message: Record<string, unknown> = {
                    type: 'RX36_GAME_TOKEN',
                    token: payload.token,
                    backend_base_url: payload.backend_base_url,
                    nonce,
                };
                if (typeof payload.exp === 'number' && Number.isFinite(payload.exp)) {
                    message.exp = payload.exp;
                }
                if (typeof payload.sub_hint === 'string' && payload.sub_hint.trim().length > 0) {
                    message.sub_hint = payload.sub_hint;
                }
                if (typeof payload.username_hint === 'string' && payload.username_hint.trim().length > 0) {
                    message.username_hint = payload.username_hint;
                }

                iframe.contentWindow?.postMessage(message, event.origin);

                const presenceState = (window as Window & {
                    __rx36PresenceStatus?: PresenceEnvelope;
                }).__rx36PresenceStatus;

                if (presenceState?.messageType) {
                    iframe.contentWindow?.postMessage(
                        {
                            type: presenceState.messageType,
                            ...presenceState.payload,
                        },
                        event.origin,
                    );
                }
            } catch (error) {
                const errorCode = error instanceof Error && error.message
                    ? error.message
                    : 'token_unavailable';
                iframe.contentWindow?.postMessage(
                    { type: 'RX36_GAME_TOKEN_ERROR', error: errorCode, nonce },
                    event.origin,
                );
            }
        }
    });
}
